'use client';

import clsx from 'clsx';
import { type ReactNode, useId } from 'react';
import { X } from 'react-bootstrap-icons';

import { SDSInternalDialog, type SDSInternalDialogProps } from '@repo/sds/components/_internal/SDSInternalDialog';
import { SDSButton } from '@repo/sds/components/SDSButton';
import { SDSButtonGroup } from '@repo/sds/components/SDSButtonGroup';
import { SDSFlexbox } from '@repo/sds/components/SDSFlexbox';
import { SDSIconButton } from '@repo/sds/components/SDSIconButton';
import { SDSText } from '@repo/sds/components/SDSText';

import '@repo/sds/components/SDSDialog/SDSDialog.scss';

import { DataTestIds } from '@repo/frontend-shared';

export interface SDSDialogProps {
  // Core props
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;

  // Header props
  title?: string;
  /** Rendered above the title, e.g. a lock or status badge. */
  titleIcon?: ReactNode;
  titleTestId?: string;
  showCloseButton?: boolean;
  closeButtonTestId?: string;

  // Size variants
  size?: 'small' | 'medium' | 'large' | 'fullscreen';

  // Footer props
  primaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    disabled?: boolean;
    dataTestId?: string;
    status?: 'alert' | 'info' | 'warning';
    ariaLabel?: string;
    leftIcon?: ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    dataTestId?: string;
    status?: 'alert' | 'info' | 'warning';
    ariaLabel?: string;
    leftIcon?: ReactNode;
  };

  // Styling
  className?: string;
  containerClassName?: string;
  dataTestId?: string;

  // Backdrop
  hasBackdrop?: boolean;
  backdropClassName?: string;

  // Accessibility
  /** Id of the element that describes the dialog (announced after the title). */
  ariaDescribedBy?: string;
  /** Where focus goes on close. Defaults to the element focused before the dialog opened. */
  finalFocus?: SDSInternalDialogProps['finalFocus'];
}

export function SDSDialog({
  isOpen,
  onClose,
  children,
  title,
  titleIcon,
  titleTestId,
  showCloseButton = true,
  closeButtonTestId,
  size = 'medium',
  primaryAction,
  secondaryAction,
  className,
  containerClassName,
  dataTestId,
  hasBackdrop = true,
  backdropClassName,
  ariaDescribedBy,
  finalFocus,
}: SDSDialogProps) {
  const dialogClasses = clsx('SDSDialog', `SDSDialog--${size}`, className);

  const backdropClasses = clsx(
    'SDSDialog__backdrop',
    {
      'SDSDialog__backdrop--transparent': !hasBackdrop,
    },
    backdropClassName,
  );

  const containerClasses = clsx('SDSDialog__container', containerClassName);

  // Determine if we need to show the close button in the header
  // If there's a title, we handle the close button ourselves
  // If there's no title but showCloseButton is true, let SDSInternalDialog handle it
  const showInternalCloseButton = !title && showCloseButton;
  const showHeaderCloseButton = title && showCloseButton;

  const titleId = useId();
  const resolvedCloseButtonTestId = closeButtonTestId ?? DataTestIds.COMMON.MODAL_CLOSE_ICON;

  return (
    <SDSInternalDialog
      isOpen={isOpen}
      onClose={onClose}
      hasBackdrop={hasBackdrop}
      backdropClassName={backdropClasses}
      containerClassName={containerClasses}
      dialogClassName={dialogClasses}
      showCloseButton={showInternalCloseButton}
      closeButtonTestId={resolvedCloseButtonTestId}
      dataTestId={dataTestId ?? DataTestIds.SDS.SDS_DIALOG}
      ariaLabelledBy={title ? titleId : undefined}
      ariaDescribedBy={ariaDescribedBy}
      finalFocus={finalFocus}
    >
      <SDSFlexbox direction="column" gap="content-large">
        <SDSFlexbox direction="column" gap="text-medium" alignItems="flex-start">
          {/* Header - only render if title or header close button needed */}
          {(title || showHeaderCloseButton) && (
            <SDSFlexbox alignItems="flex-start" className="SDSDialog__header">
              {title && (
                <SDSFlexbox direction="column" alignItems="flex-start" gap="text-medium" className="SDSDialog__title">
                  {titleIcon}
                  <SDSText type="h4" text={title} isInline id={titleId} dataTestId={titleTestId} />
                </SDSFlexbox>
              )}
              {showHeaderCloseButton && (
                <SDSIconButton
                  className="SDSDialog__close"
                  icon={<X />}
                  size="big"
                  onClick={onClose}
                  ariaLabel="Close dialog"
                  dataTestId={resolvedCloseButtonTestId}
                />
              )}
            </SDSFlexbox>
          )}
          <div className="SDSDialog__body">{children}</div>
        </SDSFlexbox>
        {/* Body - always render, contains children */}

        {/* Footer - only render if actions provided */}
        {(primaryAction || secondaryAction) && (
          <SDSFlexbox
            fullWidth
            gap="text-large"
            alignItems="center"
            justifyContent="flex-end"
            className="SDSDialog__footer"
          >
            <SDSFlexbox
              gap="button-medium"
              alignItems="center"
              justifyContent="center"
              className="SDSDialog__actionsWrapper"
            >
              <SDSButtonGroup justifyContent="flex-end" isFullWidth={true}>
                {secondaryAction && (
                  <SDSButton
                    mode="outline"
                    onClick={secondaryAction.onClick}
                    text={secondaryAction.label}
                    leftIcon={secondaryAction.leftIcon}
                    dataTestId={secondaryAction.dataTestId}
                    status={secondaryAction.status}
                    ariaLabel={secondaryAction.ariaLabel}
                  />
                )}
                {primaryAction && (
                  <SDSButton
                    mode="primary"
                    onClick={primaryAction.onClick}
                    isSubmitting={primaryAction.loading}
                    isDisabled={primaryAction.loading ? false : primaryAction.disabled}
                    text={primaryAction.label}
                    leftIcon={primaryAction.leftIcon}
                    dataTestId={primaryAction.dataTestId}
                    status={primaryAction.status}
                    ariaLabel={primaryAction.ariaLabel}
                  />
                )}
              </SDSButtonGroup>
            </SDSFlexbox>
          </SDSFlexbox>
        )}
      </SDSFlexbox>
    </SDSInternalDialog>
  );
}
